import { FilterLines, Link01 } from '@timelyapp/icons/line-icons';
import { CheckCircle, HelpCircle as HelpCircleSolid } from '@timelyapp/icons/solid-icons';
import { Button, DropdownMenu, type DropdownMenuItem, Text, Tooltip } from '@timelyapp/react';
import classNames from 'classnames';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { isLinkedWorkItemUri } from '~/api/linkedWorkItems/linkedWorkItems.helpers';
import ClearableButton from '~/components/buttons/ClearableButton';
import type { DropdownOption, OptionMeta } from '~/components/DropdownBuilder';
import DropdownBuilder from '~/components/DropdownBuilder';
import { HourSyncStatus } from '~/components/HourSyncStatus';
import { Close } from '~/components/Icons';
import { LinkedWorkIcon } from '~/components/LinkedWorkIcon';
import { getContextualTicAccounts } from '~/redux/modules/tic/tic.auth.selectors';
import { getHasLinkedWorkItemsSupport } from '~/redux/selectors/accounts';
import type { HourSyncStatus as HourSyncStatusType } from '~/types/syncStatus';
import { ucFirst } from '~/utils/text-helper';
import { useIsMobile } from '~/utils/viewport';
import { resolveLinkedWorkEmptyLabel } from '~redux/modules/tic/tasks/linkedWorkEmptyState';
import {
  deriveStatusFilterChip,
  type TicLinkedWorkMode,
} from '~redux/modules/tic/tasks/tic.tasks.scope';
import { useFetchMissingLinkedResources } from '../hooks/useFetchMissingLinkedResources';
import { useLinkedWorkOptions } from '../hooks/useLinkedWorkOptions';
import { LinkedWorkPreview } from './TimesheetProposal.LinkedWorkPreview';
import styles from './TimesheetProposal.LinkedWorkSelect.module.scss';

interface LinkedWorkSelectProps {
  value: string | null;
  /** Content of the target when no linked work is selected. */
  placeholder?: React.ReactNode;
  onChange: (resourceId: string | null) => void;
  onOpenChange?: (isOpen: boolean) => void;
  disabled: boolean;
  /**
   * The entry/row's Timely project id. Drives auto-filtering of the linked-work list by the
   * selected parent project (and the rest of the scope/status filters), matching the manual-logging
   * picker. Omit in project-less contexts (e.g. batch reassign) to list all integrations.
   */
  projectId?: number | null;
  shouldAutoFocus?: boolean;
  onClearAutoFocus?: () => void;
  popoverFixed?: boolean;
  /** Render the dropdown list in a portal so it escapes a clipping/overflow container. */
  usePortal?: boolean;
  /** Apply button-style borders and styling */
  asButton?: boolean;
  /** "icon" renders an icon-only target that expands to show the title on hover. */
  display?: 'default' | 'icon' | 'pill';
  /**
   * Replace the built-in target entirely, e.g. with a shared Button in a toolbar. The
   * picker still owns opening, selection and clearing; the target only shows the state.
   */
  renderTarget?: (state: {
    onClick: () => void;
    isOpen: boolean;
    /** The selected item's title, or the empty-state wording. */
    label: string;
    selected: boolean;
  }) => React.ReactElement;
  /** Sync status to display when submitted (disabled) */
  syncStatus?: HourSyncStatusType[];
}

interface LinkedWorkDropdownOption extends DropdownOption<string> {
  key?: string;
  toolId?: string;
  toolName?: string;
  toolIconUrl?: string;
  isCompleted?: boolean;
  description?: string;
  url?: string;
}

// Scope is a mutually-exclusive choice; 'client' is reserved for PR2 and stays out of the UI.
type ScopeValue = Exclude<TicLinkedWorkMode, 'client'>;
type ScopeState = 'active' | 'inactive' | 'unavailable';
const SCOPE_VALUES: Array<ScopeValue> = ['all', 'project', 'integration'];
const LINKED_WORK_OPTION_HEIGHT = 32;
const LINKED_WORK_MAX_HEIGHT = 300;

// When this entry has no project yet, the project/integration scopes can't name a specific project
// or integration. Copy stays neutral and forward-looking — it never interpolates an integration name.
const NO_PROJECT_PROJECT_TOOLTIP = "Once this entry has a project, shows only that project's work.";
const NO_PROJECT_INTEGRATION_TOOLTIP =
  'Once this entry has a project, shows everything from the same integration.';

interface ScopeInfo {
  label: string;
  state: ScopeState;
  tooltip: string | null;
}

// Per-scope copy, resolved against the current entry's project context. Mirrors the manual-logging
// picker so the two views stay in sync.
const getScopeInfo = (
  value: ScopeValue,
  ctx: { isProjectSelected: boolean; integrationName: string | null }
): ScopeInfo => {
  if (value === 'project') {
    return {
      label: ctx.integrationName ? `Selected ${ctx.integrationName} project` : 'Selected project',
      state: ctx.isProjectSelected && !!ctx.integrationName ? 'active' : 'unavailable',
      tooltip: ctx.isProjectSelected
        ? 'Only work mapped to the chosen project.'
        : NO_PROJECT_PROJECT_TOOLTIP,
    };
  }
  if (value === 'integration') {
    if (!ctx.isProjectSelected) {
      return {
        label: 'Same source as project',
        state: 'unavailable',
        tooltip: NO_PROJECT_INTEGRATION_TOOLTIP,
      };
    }
    if (ctx.integrationName == null) {
      return {
        label: 'This integration',
        state: 'inactive',
        tooltip: "This entry's project isn't linked to an integration.",
      };
    }
    return {
      label: `Everything from ${ctx.integrationName}`,
      state: 'active',
      tooltip: `All work you can access from ${ctx.integrationName}.`,
    };
  }
  return {
    label: 'Show everything',
    state: 'active',
    tooltip: 'Every linked task, across all your integrations.',
  };
};

// A small ⓘ affordance whose explanation pops on hover (mouse-first; opens on focus too). Injected
// into a menu row's right slot via `sidenote`. Portaled so the bubble escapes the menu's clipping;
// stopPropagation keeps a click on the ⓘ from toggling the row.
const InfoTooltipIcon = ({ tooltip }: { tooltip: string }) => (
  <Tooltip
    tooltip={tooltip}
    position="top"
    usePortal
    openOnFocus
    delay={150}
    element="span"
    childrenClassName={styles.infoIcon}
    onClick={event => event.stopPropagation()}
  >
    <HelpCircleSolid width={14} height={14} />
  </Tooltip>
);

export const TimesheetProposalLinkedWorkSelect: React.FC<LinkedWorkSelectProps> = ({
  value,
  placeholder,
  onChange,
  onOpenChange,
  disabled,
  projectId,
  shouldAutoFocus = false,
  onClearAutoFocus,
  popoverFixed = false,
  usePortal = false,
  asButton = false,
  display = 'default',
  syncStatus,
  renderTarget: renderCustomTarget,
}) => {
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const isFilterOpenRef = useRef(false);
  const isPreviewOpenRef = useRef(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const handlePreviewCloseAutoFocus = useCallback((event: Event) => {
    if (searchInputRef.current) {
      event.preventDefault();
      searchInputRef.current.focus();
    }
  }, []);
  const handlePreviewOpenChange = useCallback((open: boolean) => {
    isPreviewOpenRef.current = open;
  }, []);
  const filterListMinHeightRef = useRef(LINKED_WORK_OPTION_HEIGHT);

  // Pass search query + project context to the hook for API search and scope/status filtering.
  const {
    groupedOptions,
    flatOptions,
    isLoading: isSearching,
    hasMore,
    loadMore,
    refresh,
    scrollResetKey,
    isResolving,
    error: browseError,
    filters,
  } = useLinkedWorkOptions(searchQuery.trim() || undefined, { projectId });

  // Find selected option (from the UNFILTERED flat list, so an active scope never hides the chip).
  const selectedOption = useMemo(() => {
    return flatOptions.find(opt => opt.id === value);
  }, [flatOptions, value]);

  // When the selected task isn't found in the accessible task list (e.g. Jira connection
  // without reference_tasks, removed subscription), attempt a direct fetch by ID.
  // Once the fetch settles we stop showing "Loading..." and show a static fallback instead.
  const { workspace } = useSelector(getContextualTicAccounts);
  const resourceIdsToFetch = useMemo(
    () => (value != null && !selectedOption ? [value] : []),
    [value, selectedOption]
  );
  const { attemptedIdentifiers } = useFetchMissingLinkedResources({
    resourceIds: resourceIdsToFetch,
  });
  // Each source's lookup runs on its own terms. A native linked-work uri resolves against Rails,
  // which needs no workspace (nor a loaded pool) but does need the `api_linked_work` feature;
  // every TIC lookup is skipped, and can never resolve, without a TIC workspace.
  const hasLinkedWorkItemsSupport = useSelector(getHasLinkedWorkItemsSupport);
  const noLookupWillRun = isLinkedWorkItemUri(value)
    ? !hasLinkedWorkItemsSupport
    : workspace == null;
  // A fetch has settled when:
  // - The task was found in the accessible list (selectedOption != null)
  // - No lookup will ever run for this value (see `noLookupWillRun` above)
  // - In edit mode: initial tasks loaded and task not found (user can still search manually)
  // - In submitted mode: the specific ID was explicitly fetched and confirmed missing
  const fetchSettled =
    selectedOption != null ||
    noLookupWillRun ||
    (!disabled && !isSearching) ||
    attemptedIdentifiers.has(value ?? '');

  // Transform grouped options into DropdownBuilder format
  const dropdownOptions = useMemo<LinkedWorkDropdownOption[]>(() => {
    return groupedOptions.map(group => ({
      value: `header-${group.groupKey}`,
      label: group.toolName,
      toolId: group.toolId,
      toolName: group.toolName,
      toolIconUrl: group.toolIconUrl,
      children: group.options.map(opt => ({
        value: opt.id,
        label: opt.label,
        key: opt.key,
        toolId: opt.toolId,
        toolName: opt.toolName,
        toolIconUrl: opt.toolIconUrl,
        isCompleted: opt.isCompleted,
        description: opt.description,
        url: opt.url,
      })),
    }));
  }, [groupedOptions]);

  const handleChange = useCallback(
    (optionValue: string | null) => {
      onChange(optionValue);
      setSearchQuery('');
      setIsOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onChange(null);
      setSearchQuery('');
    },
    [onChange]
  );

  const handleOpenChange = useCallback(
    (newIsOpen: boolean) => {
      // Keep the list open while the filter menu is open — a click inside the filter popover
      // shouldn't dismiss the linked-work list underneath it.
      if (!newIsOpen && (isFilterOpenRef.current || isPreviewOpenRef.current)) {
        return;
      }
      setIsOpen(newIsOpen);
      if (newIsOpen) {
        refresh();
      }
      if (!newIsOpen && shouldAutoFocus && onClearAutoFocus) {
        onClearAutoFocus();
      }
      if (!newIsOpen) {
        setSearchQuery('');
      }
      onOpenChange?.(newIsOpen);
    },
    [shouldAutoFocus, onClearAutoFocus, onOpenChange, refresh]
  );

  const handleFilterOpenChange = useCallback(
    (nextIsOpen: boolean) => {
      if (nextIsOpen) {
        const rowCount = dropdownOptions.reduce(
          (count, group) => count + 1 + (group.children?.length ?? 0),
          0
        );
        filterListMinHeightRef.current = Math.min(
          LINKED_WORK_MAX_HEIGHT,
          LINKED_WORK_OPTION_HEIGHT * Math.max(1, rowCount)
        );
      }
      isFilterOpenRef.current = nextIsOpen;
      setIsFilterOpen(nextIsOpen);
      // Downshift handles Escape on the search input, not the Filter trigger.
      if (
        !nextIsOpen &&
        document.activeElement instanceof HTMLElement &&
        document.activeElement.closest('[role="menu"]')
      ) {
        searchInputRef.current?.focus();
      }
    },
    [dropdownOptions]
  );

  // --- Filter state (scope + status), mirroring the manual-logging picker -------------------
  const {
    linkedWorkMode,
    showActive,
    showCompleted,
    isProjectSelected,
    sameIntegrationIsApplied,
    sameProjectIsApplied,
    selectedProjectToolId,
    setLinkedWorkMode,
    setShowActive,
    setShowCompleted,
  } = filters;

  const integrationName = selectedProjectToolId ? ucFirst(selectedProjectToolId) : null;

  const { isStatusFilterDefault, statusChipLabel } = deriveStatusFilterChip({
    showActive,
    showCompleted,
  });

  const handleClearStatusFilter = useCallback(() => {
    setShowActive(true);
    setShowCompleted(true);
  }, [setShowActive, setShowCompleted]);

  const activeFilterChips = useMemo(
    () => [
      ...(!isStatusFilterDefault && statusChipLabel != null
        ? [
            {
              kind: 'status' as const,
              label: statusChipLabel,
              isApplied: true,
              clearTooltip: 'Reset status filter',
              onClear: handleClearStatusFilter,
            },
          ]
        : []),
      ...(linkedWorkMode === 'integration'
        ? [
            {
              kind: 'integration' as const,
              label: 'This integration',
              isApplied: sameIntegrationIsApplied,
              clearTooltip: 'Show all linked work',
              onClear: () => setLinkedWorkMode('all'),
            },
          ]
        : []),
      ...(linkedWorkMode === 'project'
        ? [
            {
              kind: 'project' as const,
              label: 'This project',
              isApplied: sameProjectIsApplied,
              clearTooltip: 'Show all linked work',
              onClear: () => setLinkedWorkMode('all'),
            },
          ]
        : []),
    ],
    [
      isStatusFilterDefault,
      statusChipLabel,
      handleClearStatusFilter,
      linkedWorkMode,
      sameIntegrationIsApplied,
      sameProjectIsApplied,
      setLinkedWorkMode,
    ]
  );

  const filterMenuItems = useMemo((): DropdownMenuItem[] => {
    const statusItems: DropdownMenuItem[] = [
      { type: 'label', label: 'Show work with status', indent: false },
      {
        type: 'switch',
        'data-testid': 'linked-work-status-open',
        label: 'Open',
        checked: showActive,
        onCheckedChange: setShowActive,
        sidenote: <InfoTooltipIcon tooltip="Not yet done, closed, or resolved." />,
      },
      {
        type: 'switch',
        'data-testid': 'linked-work-status-done',
        label: 'Done',
        checked: showCompleted,
        onCheckedChange: setShowCompleted,
        sidenote: <InfoTooltipIcon tooltip="Marked done, closed, or resolved." />,
      },
      { type: 'separator' },
    ];

    const scopeCtx = { isProjectSelected, integrationName };

    return [
      ...statusItems,
      {
        type: 'radioGroup',
        label: 'Filter results',
        currentValue: linkedWorkMode,
        onValueChange: nextValue => setLinkedWorkMode(nextValue as TicLinkedWorkMode),
        subOptions: SCOPE_VALUES.map(scopeValue => {
          const info = getScopeInfo(scopeValue, scopeCtx);
          return {
            type: 'radioItem' as const,
            value: scopeValue,
            label:
              info.state === 'active' ? (
                info.label
              ) : (
                <span className={styles.mutedScopeLabel}>{info.label}</span>
              ),
            sidenote: info.tooltip ? <InfoTooltipIcon tooltip={info.tooltip} /> : undefined,
          };
        }),
      },
    ];
  }, [
    showActive,
    showCompleted,
    setShowActive,
    setShowCompleted,
    isProjectSelected,
    integrationName,
    linkedWorkMode,
    setLinkedWorkMode,
  ]);

  const renderOption = useCallback(
    (item: LinkedWorkDropdownOption, _index: number, _meta: OptionMeta) => {
      // Group headers (items with children)
      if (item.children) {
        return (
          <div className={styles.groupHeader}>
            {item.toolIconUrl && (
              <LinkedWorkIcon
                src={item.toolIconUrl}
                toolId={item.toolId}
                alt={item.toolName}
                className={styles.toolIcon}
              />
            )}
            <span className={styles.groupHeaderText}>{item.label}</span>
          </div>
        );
      }

      // Regular options
      return (
        <LinkedWorkPreview
          item={item}
          openOnClick={false}
          onOpenChange={handlePreviewOpenChange}
          onCloseAutoFocus={handlePreviewCloseAutoFocus}
        >
          <div className={styles.optionContent} tabIndex={0}>
            {item.toolIconUrl && (
              <LinkedWorkIcon
                src={item.toolIconUrl}
                toolId={item.toolId}
                alt={item.toolName}
                className={styles.toolIconSmall}
              />
            )}
            <div className={styles.optionText}>
              <div className={styles.optionTitle}>{item.label}</div>
              {item.key && <div className={styles.optionKey}>{item.key}</div>}
            </div>
            {item.isCompleted && <CheckCircle className={styles.completedIcon} />}
          </div>
        </LinkedWorkPreview>
      );
    },
    [handlePreviewOpenChange, handlePreviewCloseAutoFocus]
  );

  const matchOption = useCallback(
    (option: LinkedWorkDropdownOption, filterString: string): boolean => {
      if (!filterString) return true;
      const search = filterString.toLowerCase();
      const labelMatch = option.label?.toLowerCase().includes(search);
      const keyMatch = option.key?.toLowerCase().includes(search);
      return labelMatch || keyMatch || false;
    },
    []
  );

  const renderTarget = useCallback(
    (onClick: () => void) => {
      if (renderCustomTarget) {
        return renderCustomTarget({
          onClick,
          isOpen,
          selected: !!selectedOption,
          label: selectedOption
            ? selectedOption.label
            : value != null
              ? fetchSettled
                ? 'Linked item'
                : 'Loading...'
              : 'Add linked work',
        });
      }
      if (selectedOption) {
        return (
          <LinkedWorkPreview
            item={selectedOption}
            openOnClick={disabled}
            onOpenChange={handlePreviewOpenChange}
            onCloseAutoFocus={handlePreviewCloseAutoFocus}
          >
            <div
              className={classNames(styles.selectedTarget, {
                [styles.disabled]: disabled,
                [styles.asButton]: asButton,
                [styles.iconMode]: display === 'icon',
                [styles.pillMode]: display === 'pill',
              })}
            >
              <button
                type="button"
                className={styles.selectionButton}
                onClick={() => {
                  isPreviewOpenRef.current = false;
                  onClick();
                }}
                aria-label={`${disabled ? 'Linked work' : 'Change linked work'}: ${selectedOption.key} ${selectedOption.label}`}
                aria-expanded={disabled ? undefined : isOpen}
              >
                {selectedOption.toolIconUrl && (
                  <LinkedWorkIcon
                    src={selectedOption.toolIconUrl}
                    toolId={selectedOption.toolId}
                    // The icon-only target has nothing else visible until hover, so it must never
                    // render empty.
                    fallbackElement={<Link01 className={styles.toolIconSmall} />}
                    alt={selectedOption.toolName}
                    className={styles.toolIconSmall}
                  />
                )}
                <span className={styles.selectedText}>
                  <span className={styles.selectedTitle}>{selectedOption.label}</span>
                  <span className={styles.selectedKey}>{selectedOption.key}</span>
                </span>
              </button>
              {!disabled && display !== 'pill' && (
                <button
                  type="button"
                  className={styles.clearButton}
                  onClick={handleClear}
                  aria-label="Clear linked work"
                >
                  <Close width={10} height={10} />
                </button>
              )}
            </div>
          </LinkedWorkPreview>
        );
      }

      return (
        <div
          className={classNames(styles.placeholder, {
            [styles.disabled]: disabled,
            [styles.asButton]: asButton,
            [styles.iconMode]: display === 'icon',
            [styles.pillMode]: display === 'pill',
          })}
          data-empty-assignment={display === 'pill' && value == null ? true : undefined}
          role={display === 'pill' ? 'button' : undefined}
          tabIndex={display === 'pill' && !disabled ? 0 : undefined}
          onKeyDown={event => {
            if (display === 'pill' && !disabled && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              onClick();
            }
          }}
          onClick={onClick}
        >
          {value == null && placeholder ? (
            placeholder
          ) : (
            <>
              <Link01 className={styles.placeholderIcon} />
              <span className={styles.linkedWorkLabel}>
                <Text.Label size="small">
                  {value != null
                    ? fetchSettled
                      ? 'Linked item'
                      : 'Loading...'
                    : display === 'pill'
                      ? 'Link work'
                      : 'Add linked work'}
                </Text.Label>
              </span>
            </>
          )}
        </div>
      );
    },
    [
      renderCustomTarget,
      selectedOption,
      placeholder,
      disabled,
      asButton,
      display,
      value,
      handleClear,
      fetchSettled,
      isOpen,
      handlePreviewOpenChange,
      handlePreviewCloseAutoFocus,
    ]
  );

  if (disabled) {
    return (
      <div className={styles.submittedContainer}>
        {syncStatus && syncStatus.length > 0 && (
          <div style={{ paddingLeft: 4 }}>
            <HourSyncStatus status={syncStatus} />
          </div>
        )}
        <div style={{ position: 'relative', minWidth: 0 }}>{renderTarget(() => {})}</div>
      </div>
    );
  }

  return (
    <DropdownBuilder<string, LinkedWorkDropdownOption>
      value={value}
      options={dropdownOptions}
      onChange={handleChange}
      isOpen={isOpen}
      onSetIsOpen={handleOpenChange}
      filterString={searchQuery}
      onSetFilterString={setSearchQuery}
      renderOption={renderOption}
      getOptionTitle={() => ''}
      matchOption={matchOption}
      onReachEnd={hasMore ? loadMore : undefined}
      scrollResetKey={scrollResetKey}
      placeholder={isSearching ? 'Searching...' : 'Search'}
      optionHeight={LINKED_WORK_OPTION_HEIGHT}
      popoverFixed={popoverFixed}
      isLoadingOptions={isSearching && searchQuery.trim().length > 0}
      inputCaret={false}
      clearable
      maxHeight={LINKED_WORK_MAX_HEIGHT}
      noResultsLabel={resolveLinkedWorkEmptyLabel({
        hasError: browseError != null,
        isResolving,
        emptyLabel: 'No linked work items found',
      })}
    >
      {context => (
        <div
          data-testid="linked-work-picker"
          className={classNames({ [styles.pillPicker]: display === 'pill' })}
          style={{ position: 'relative', minWidth: 0, width: '100%', height: '100%' }}
        >
          {renderTarget(() => handleOpenChange(!isOpen))}
          {context.isOpen &&
            context.renderListPopover({
              usePortal,
              popoverPosition: 'bottom-start',
              popoverListFooter:
                display === 'pill' && value && !disabled ? (
                  <button
                    type="button"
                    className={styles.removeAssignment}
                    onClick={() => handleChange(null)}
                  >
                    Remove linked work
                  </button>
                ) : undefined,
              popoverListHeader: (
                <div data-testid="linked-work-popover" className={styles.dropdownHeader}>
                  <div className={styles.headerTop}>
                    <span className={styles.headerTitle}>Linked Work</span>
                    <DropdownMenu
                      open={isFilterOpen}
                      onOpenChange={handleFilterOpenChange}
                      modal={false}
                      returnFocusOnClose={false}
                      closeOnSelect={false}
                      items={filterMenuItems}
                      contentClassName={styles.filterMenuContent}
                      side="bottom"
                      align="end"
                      sideOffset={4}
                      width={290}
                      trigger={
                        <Button
                          data-testid="linked-work-filter-button"
                          size="xsmall"
                          priority="xlow"
                          isActive={isFilterOpen}
                          className={styles.filterButton}
                        >
                          <FilterLines width={14} height={14} />
                          Filter
                        </Button>
                      }
                    />
                  </div>
                  {activeFilterChips.length > 0 && (
                    <div className={styles.headerChips}>
                      {activeFilterChips.map(chip => (
                        <ClearableButton
                          key={chip.kind}
                          size="xsmall"
                          priority={chip.isApplied ? 'medium' : 'xlow'}
                          selected={chip.isApplied}
                          isActive={null}
                          className={classNames({
                            [styles.inactiveFilterChip]: !chip.isApplied,
                          })}
                          onClear={chip.onClear}
                          clearTooltip={chip.clearTooltip}
                        >
                          {chip.label}
                        </ClearableButton>
                      ))}
                    </div>
                  )}
                  {selectedOption && (
                    <section className={styles.currentSelection} aria-label="Current linked work">
                      <Text.Label size="xsmall">Current linked work</Text.Label>
                      <LinkedWorkPreview
                        item={selectedOption}
                        onOpenChange={handlePreviewOpenChange}
                        onCloseAutoFocus={handlePreviewCloseAutoFocus}
                      >
                        <button
                          type="button"
                          className={styles.currentSelectionRow}
                          aria-label={`Linked work: ${selectedOption.key} ${selectedOption.label}`}
                        >
                          <div className={styles.currentSelectionText}>
                            <span className={styles.currentSelectionTitle}>
                              {selectedOption.label}
                            </span>
                            {selectedOption.key && (
                              <span className={styles.currentSelectionKey}>
                                {selectedOption.key}
                              </span>
                            )}
                          </div>
                        </button>
                      </LinkedWorkPreview>
                    </section>
                  )}
                  <div className={styles.searchContainer}>
                    <svg
                      className={styles.searchIcon}
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <input
                      {...context.downshift.getInputProps({
                        placeholder: isSearching ? 'Searching...' : 'Search',
                        className: styles.searchInput,
                        autoFocus: !isMobile,
                        ref: searchInputRef,
                        onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                          // Preview focus must not schedule Downshift's delayed blur dismissal.
                          if (
                            isPreviewOpenRef.current ||
                            (event.relatedTarget instanceof HTMLElement &&
                              event.relatedTarget.closest('[data-linked-work-preview]'))
                          ) {
                            Object.assign(event, { preventDownshiftDefault: true });
                          }
                        },
                      })}
                    />
                  </div>
                </div>
              ),
              renderListProps: {
                width: 'min(350px, calc(100vw - 24px))',
                className: styles.optionsList,
                minHeight: isFilterOpen ? filterListMinHeightRef.current : undefined,
              },
            })}
        </div>
      )}
    </DropdownBuilder>
  );
};
